// EDEN · calendar render module (pure HTML-string builders — no fetch, no listeners). The four
// zoom levels of ONE academic calendar: Week (agenda per day + your lessons) · Month (7-col grid
// with entry dots) · Term (the week-strip, 1..week_count) · Year (12 mini-months over the academic
// year). src/pages/calendar.ts owns state + events; this module only draws. Rendering matched to
// horizon (NN/g): grid for near-term detail, compact glance for long ranges. Colour is never the
// only signal — every entry keeps a text label (WCAG 1.4.1).
//
// Multi-day entries (end_date, 0019): drawn as a span, never repeated once per day. Month draws a
// connected bar across the day cells it touches (no grid-line math needed — each cell renders its
// own segment, rounded only at the true start/end, and bridges the grid gap into its neighbour so
// adjacent segments read as one continuous bar). Week lists a span ONCE, above the per-day agenda,
// as its own date-range row. Term shows it in every week-row it overlaps (week-strip granularity
// already reads as "spanning" once it appears in more than one row). Year expands it across every
// day it covers before building the dominant-dot map — a lit streak of days IS the span, at that
// zoom. Key events (is_key, 0019, Lead-only to set): a distinct pinned chip + legend in Year view;
// everywhere else that already renders a type chip gets a quiet `.key` modifier for free.

import { esc } from "./dom";
import { t, type StringKey } from "./i18n";
import { termWeekRange, weekOf } from "./term-weeks";

export type ViewKind = "week" | "month" | "term" | "year";

export interface Entry {
  id: string;
  entry_date: string; // YYYY-MM-DD
  end_date: string | null; // YYYY-MM-DD, >= entry_date; null = single-day (0019)
  label: string;
  type: "teaching" | "test" | "event" | "holiday" | "training";
  is_key: boolean; // 0019 — Lead-only to set/clear
  grades: number[] | null; // 0021 — null = every grade
  teaching_day: boolean; // 0021 — does teaching happen this day
}
export interface WeekLesson {
  lesson_id: string;
  topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  class_name: string;
  plan_id: string | null;
}
export interface TermLike {
  name: string;
  start_date: string;
  end_date: string;
  week_count: number;
}

// ── date helpers (UTC-noon anchored — mirrors _lib/time.ts; never local Date(iso) for placement) ──
function parse(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12));
}
function toIso(dt: Date): string {
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(
    dt.getUTCDate(),
  ).padStart(2, "0")}`;
}
function addDays(dt: Date, n: number): Date {
  const c = new Date(dt.getTime());
  c.setUTCDate(c.getUTCDate() + n);
  return c;
}
/** Mon=0 … Sun=6 (the calendar grid is Monday-first). */
function mondayIndex(dt: Date): number {
  return (dt.getUTCDay() + 6) % 7;
}
/** Every ISO date from `a` to `b` inclusive (a <= b). Used to expand a span onto each day it
 *  covers for views too compact to draw a real bar (Year). */
function datesBetween(a: string, b: string): string[] {
  const out: string[] = [];
  let cur = parse(a);
  const end = parse(b);
  while (cur.getTime() <= end.getTime()) {
    out.push(toIso(cur));
    cur = addDays(cur, 1);
  }
  return out;
}

/** Today's date as a Cambodia-local Y-M-D (users are UTC+7; app has no other tz). */
export function todayIso(): string {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

/** A real multi-day entry — end_date set and after entry_date. Same test everywhere so week /
 *  month / term / year agree on what counts as "a span" vs. an ordinary single-day entry. */
export function hasSpan(e: Pick<Entry, "entry_date" | "end_date">): boolean {
  return !!e.end_date && e.end_date > e.entry_date;
}

// ── the term-week rule: the school calendar's weeks, one module shared with the server ──
export { termWeekRange, weekOf };

/** Where the Week view's ‹ / › lands from week `weekNo`: the start of the term's previous or next
 *  week. School-calendar weeks run a day to a fortnight, so a fixed 7-day step would show a long
 *  week twice. Before Week 1 it is the day before (the previous term's last week); after the term's
 *  last week, the next term's start — or, with no next term, the last week again. */
export function adjacentWeekAnchor(term: TermLike, weekNo: number, dir: -1 | 1, nextStart: string | null): string {
  if (dir === 1) {
    return weekNo >= term.week_count ? nextStart ?? termWeekRange(term, weekNo).start : termWeekRange(term, weekNo + 1).start;
  }
  return weekNo <= 1 ? toIso(addDays(parse(termWeekRange(term, 1).start), -1)) : termWeekRange(term, weekNo - 1).start;
}

// ── formatting ──
const wd = (dt: Date): string => t(`wd.${dt.getUTCDay()}` as StringKey);
const mo = (m: number): string => t(`mo.${m + 1}` as StringKey);
const typeLabel = (ty: Entry["type"]): string => t(`type.${ty}` as StringKey);

/** "Wed 12 Jun" — the shared calendar date format (promoted from calendar.ts / this-week.ts). */
export function fmtDate(iso: string): string {
  const dt = parse(iso);
  return `${wd(dt)} ${dt.getUTCDate()} ${mo(dt.getUTCMonth())}`;
}
function fmtShort(iso: string): string {
  const dt = parse(iso);
  return `${dt.getUTCDate()} ${mo(dt.getUTCMonth())}`;
}
/** The full date range for a span entry, or a single date for an ordinary one — used wherever an
 *  entry needs ONE line naming when it falls, span or not. */
export function fmtEntryRange(e: Pick<Entry, "entry_date" | "end_date">): string {
  return hasSpan(e) ? `${fmtShort(e.entry_date)}–${fmtShort(e.end_date as string)}` : fmtDate(e.entry_date);
}
/** "Wed 12 Jun, 14:32" (viewer-local) from a D1 `datetime('now')` string ("YYYY-MM-DD HH:MM:SS",
 *  UTC) — for the calendar_change log's "edited by {name} · {when}" line. */
export function fmtWhen(sqlDatetime: string): string {
  const dt = new Date(sqlDatetime.replace(" ", "T") + "Z");
  if (Number.isNaN(dt.getTime())) return sqlDatetime;
  const hh = String(dt.getHours()).padStart(2, "0");
  const mm = String(dt.getMinutes()).padStart(2, "0");
  return `${fmtDate(toIso(new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12))))}, ${hh}:${mm}`;
}

function groupByDate(entries: Entry[]): Map<string, Entry[]> {
  const map = new Map<string, Entry[]>();
  for (const e of entries) (map.get(e.entry_date) ?? map.set(e.entry_date, []).get(e.entry_date)!).push(e);
  return map;
}
/** Spread each span onto every day it covers — the dot-glance views (Year) have no room to draw a
 *  real bar, so a lit run of days across the span IS the "one bar across the days" at that zoom. */
function expandSpans(entries: Entry[]): Entry[] {
  const out: Entry[] = [];
  for (const e of entries) {
    if (!hasSpan(e)) { out.push(e); continue; }
    for (const d of datesBetween(e.entry_date, e.end_date as string)) out.push({ ...e, entry_date: d });
  }
  return out;
}
// Salience order so breaks/exams "pop" in the dominant-dot glance.
const RANK: Record<Entry["type"], number> = { holiday: 3, training: 3, test: 2, event: 1, teaching: 0 };
function dominantType(entries: Entry[]): Entry["type"] {
  return entries.reduce((best, e) => (RANK[e.type] > RANK[best] ? e.type : best), entries[0]!.type);
}
// A key event's chip carries a pin GLYPH, not just a colour ring — colour is never the only
// signal (WCAG 1.4.1), and the type chip's own text never says "key" on its own.
export function chipHtml(e: Pick<Entry, "type" | "is_key">): string {
  const cls = `chip ${e.type}${e.is_key ? " key" : ""}`;
  const text = (e.is_key ? "📌 " : "") + typeLabel(e.type);
  return `<span class="${cls}">${text}</span>`;
}

// ── the view switcher + period navigator (same header on every view) ──
const VIEWS: { key: ViewKind; labelKey: StringKey }[] = [
  { key: "week", labelKey: "cal.view_week" },
  { key: "month", labelKey: "cal.view_month" },
  { key: "term", labelKey: "cal.view_term" },
  { key: "year", labelKey: "cal.view_year" },
];
export function switcher(view: ViewKind): string {
  const btns = VIEWS.map(
    (v) =>
      `<button type="button" role="tab" aria-selected="${v.key === view}" class="seg-btn${
        v.key === view ? " on" : ""
      }" data-view="${v.key}">${t(v.labelKey)}</button>`,
  ).join("");
  return `<div class="seg" role="tablist" aria-label="Calendar view">${btns}</div>`;
}
export function periodNav(label: string): string {
  return `<div class="cal-nav">
    <button type="button" class="pill-btn" data-nav="prev" aria-label="${esc(t("cal.prev"))}">‹</button>
    <span class="cal-period">${esc(label)}</span>
    <button type="button" class="pill-btn" data-nav="next" aria-label="${esc(t("cal.next"))}">›</button>
    <button type="button" class="pill-btn" data-nav="today">${t("cal.today")}</button>
  </div>`;
}

// ── MONTH: 7-column grid, entry dots per day + a connected span bar, drill into a day ──
export function monthGrid(anchorIso: string, entries: Entry[], today: string): string {
  const spans = entries.filter(hasSpan);
  const byDate = groupByDate(entries.filter((e) => !hasSpan(e)));
  const anchor = parse(anchorIso);
  const month = anchor.getUTCMonth();
  const first = new Date(Date.UTC(anchor.getUTCFullYear(), month, 1, 12));
  const start = addDays(first, -mondayIndex(first));
  const heads = [1, 2, 3, 4, 5, 6, 0]
    .map((d) => `<div class="cal-head">${t(`wd.${d}` as StringKey)}</div>`)
    .join("");
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i))
    .map((dt) => {
      const di = toIso(dt);
      const es = byDate.get(di) ?? [];
      const dots = es
        .slice(0, 4)
        .map((e) => `<span class="cal-dot ${e.type}" aria-hidden="true"></span>`)
        .join("");
      const more = es.length > 4 ? `<span class="cal-more">+${es.length - 4}</span>` : "";
      const touching = spans.filter((e) => di >= e.entry_date && di <= (e.end_date as string));
      const bars = touching
        .map((e, idx) => {
          const isStart = di === e.entry_date;
          const isEnd = di === e.end_date;
          const seg = isStart && isEnd ? "seg-single" : isStart ? "seg-start" : isEnd ? "seg-end" : "seg-mid";
          return `<span class="cal-span-seg ${seg} ${e.type}${e.is_key ? " key" : ""}" style="bottom:${
            4 + idx * 7
          }px" title="${esc(e.label)}" aria-hidden="true"></span>`;
        })
        .join("");
      const cls =
        "cal-cell" + (dt.getUTCMonth() === month ? "" : " adjacent") + (di === today ? " is-today" : "");
      const count = es.length + touching.length;
      const countLabel = count ? ` — ${count} ${count === 1 ? "entry" : "entries"}` : "";
      return `<button type="button" class="${cls}" data-day="${di}" aria-label="${wd(dt)} ${dt.getUTCDate()} ${mo(
        dt.getUTCMonth(),
      )}${countLabel}"><span class="cal-cell-date">${dt.getUTCDate()}</span><span class="cal-cell-dots">${dots}${more}</span>${bars}</button>`;
    })
    .join("");
  // Under the grid, the month as a plain list (2026-09-30): a dot says "something is on", the list says
  // what — so nobody has to tap every day to find the test. A row opens that week, like its day does.
  const monthStart = toIso(first);
  const monthEnd = toIso(new Date(Date.UTC(anchor.getUTCFullYear(), month + 1, 0, 12)));
  const inMonth = entries
    .filter((e) => (e.end_date ?? e.entry_date) >= monthStart && e.entry_date <= monthEnd)
    .sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  const list = inMonth.length
    ? `<ul class="cal-mlist" aria-label="${esc(t("cal.this_month"))}">${inMonth
        .map((e) => `<li><button type="button" class="cal-mrow" data-day="${e.entry_date}"><span class="cal-mdate">${esc(fmtEntryRange(e))}</span><span class="cal-mlabel">${esc(e.label)}</span>${chipHtml(e)}</button></li>`)
        .join("")}</ul>`
    : "";
  return `<div class="cal-grid">${heads}${cells}</div>${list}`;
}

// ── WEEK: spans (once, above) + an agenda per day (no time-of-day exists) + the viewer's lessons.
// A school-calendar week is however many days run from its first school day to the next week's
// (one to a fortnight across a holiday), so the agenda draws every day of the range. ──
export function weekView(
  range: { start: string; end: string },
  entries: Entry[],
  lessons: WeekLesson[],
  today: string,
): string {
  const spans = entries.filter(hasSpan);
  const byDate = groupByDate(entries.filter((e) => !hasSpan(e)));
  const spanBlock = spans.length
    ? `<div class="cal-week-spans">${spans
        .map(
          (e) =>
            `<div class="cal-row"><span class="cal-date">${esc(fmtEntryRange(e))}</span>` +
            `<span class="cal-label">${esc(e.label)}</span>${chipHtml(e)}</div>`,
        )
        .join("")}</div>`
    : "";
  const days = datesBetween(range.start, range.end)
    .map((di) => {
      const dt = parse(di);
      const es = byDate.get(di) ?? [];
      const rows = es.length
        ? es
            .map((e) => `<div class="cal-row"><span class="cal-label">${esc(e.label)}</span>${chipHtml(e)}</div>`)
            .join("")
        : `<p class="cal-day-empty">—</p>`;
      return `<section class="cal-day${di === today ? " is-today" : ""}">
        <h3 class="cal-day-h">${wd(dt)} ${dt.getUTCDate()} ${mo(dt.getUTCMonth())}</h3>${rows}</section>`;
    })
    .join("");
  const lessonBlock = lessons.length
    ? `<div class="cal-lessons"><p class="related-title">${t("cal.your_lessons")}</p>${lessons
        .map(lessonCard)
        .join("")}</div>`
    : "";
  return `${spanBlock}<div class="cal-week">${days}</div>${lessonBlock}`;
}
function lessonCard(l: WeekLesson): string {
  // A lesson whose plan never saved opens the editor (empty form) — never a dead card (mirrors this-week).
  const href = l.plan_id ? `/plan/${esc(l.plan_id)}` : `/lesson/${esc(l.lesson_id)}/edit`;
  const chip =
    l.status === "submitted" || l.status === "approved"
      ? `<span class="chip submitted">${t("common.chip_submitted")}</span>`
      : `<span class="chip">${t("common.chip_draft")}</span>`;
  return `<a class="card" data-link href="${href}"><strong>${esc(l.class_name)}</strong> · ${esc(
    l.topic,
  )} ${chip}</a>`;
}

// ── TERM: the week-strip (Week 1..week_count), current week highlighted, drill into a week ──
// A span appears in EVERY week row it overlaps — at week granularity that repetition already
// reads as "this runs across these weeks", so no separate bar treatment is needed here.
export function termStrip(term: TermLike, entries: Entry[], currentWeek: number): string {
  const byWeek = new Map<number, Entry[]>();
  for (const e of entries) {
    const w1 = weekOf(e.entry_date, term);
    const w2 = hasSpan(e) ? weekOf(e.end_date as string, term) : w1;
    for (let w = w1; w <= w2; w++) {
      if (w < 1 || w > term.week_count) continue;
      (byWeek.get(w) ?? byWeek.set(w, []).get(w)!).push(e);
    }
  }
  const rows = Array.from({ length: term.week_count }, (_, i) => i + 1)
    .map((w) => {
      const range = termWeekRange(term, w);
      const es = byWeek.get(w) ?? [];
      const chips = es
        .slice(0, 5)
        .map((e) => `<span class="chip ${e.type}${e.is_key ? " key" : ""}">${e.is_key ? "📌 " : ""}${esc(e.label)}</span>`)
        .join(" ");
      return `<button type="button" class="cal-strip-row${w === currentWeek ? " is-current" : ""}" data-week="${w}">
        <span class="cal-strip-wk">${t("common.week_n", { n: w })}</span>
        <span class="cal-strip-range">${fmtShort(range.start)}–${fmtShort(range.end)}</span>
        <span class="cal-strip-chips">${chips || '<span class="muted">—</span>'}</span></button>`;
    })
    .join("");
  return `<div class="cal-strip">${rows}</div>`;
}

// ── YEAR: 12 mini-months, dominant-type dot per day (spans expanded across their days), a pinned
// key-event chip per lit key day, and a legend list of the AY's key dates ──
export function yearGlance(startIso: string, endIso: string, entries: Entry[], today: string): string {
  const byDate = groupByDate(expandSpans(entries));
  const months: { y: number; m: number }[] = [];
  let cur = new Date(Date.UTC(parse(startIso).getUTCFullYear(), parse(startIso).getUTCMonth(), 1, 12));
  const end = parse(endIso);
  while (cur.getTime() <= end.getTime()) {
    months.push({ y: cur.getUTCFullYear(), m: cur.getUTCMonth() });
    cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1, 12));
  }
  const grid = `<div class="cal-year">${months.map(({ y, m }) => miniMonth(y, m, byDate, today)).join("")}</div>`;
  return grid + keyLegend(entries);
}
function miniMonth(year: number, month: number, byDate: Map<string, Entry[]>, today: string): string {
  const first = new Date(Date.UTC(year, month, 1, 12));
  const start = addDays(first, -mondayIndex(first));
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i))
    .map((dt) => {
      const di = toIso(dt);
      const es = dt.getUTCMonth() === month ? byDate.get(di) ?? [] : [];
      const dot = es.length ? ` cal-mini-on ${dominantType(es)}` : "";
      const key = es.some((e) => e.is_key) ? " cal-mini-key" : "";
      const out = dt.getUTCMonth() === month ? "" : " out";
      const tdy = di === today ? " is-today" : "";
      return `<span class="cal-mini-day${out}${dot}${key}${tdy}"></span>`;
    })
    .join("");
  const anchor = toIso(first);
  return `<button type="button" class="mini-month" data-month="${anchor}">
    <span class="mini-month-h">${mo(month)} ${year}</span>
    <span class="mini-grid">${cells}</span></button>`;
}
/** The AY's key dates as a short chip list — the "distinct chip" a mini-month's tiny dot can't
 *  carry text on its own (0019). Empty when nothing is flagged key yet (no legend clutter). */
function keyLegend(entries: Entry[]): string {
  const keys = [...entries].filter((e) => e.is_key).sort((a, b) => a.entry_date.localeCompare(b.entry_date));
  if (!keys.length) return "";
  const rows = keys
    .map((e) => `<span class="chip key">📌 ${esc(fmtEntryRange(e))} · ${esc(e.label)}</span>`)
    .join("");
  return `<div class="cal-keylist"><p class="cal-keylist-h">${t("cal.key_dates")}</p>${rows}</div>`;
}
